import { useEffect, useRef } from 'react';
import { shortcutFor, type ShortcutAction, type ShortcutContext } from './keyboard.js';

export type ShortcutState = Omit<ShortcutContext, 'typing' | 'modified'>;

/** Whether the keystroke belongs to a field the person is typing in. */
function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT';
}

/**
 * Room-wide key handling. A screen-shared tool is driven by one person talking
 * while they operate it, and every trip to the mouse is a pause in that.
 *
 * Shift is not treated as a modifier, since `R` is how you type `r` with caps on.
 */
export function useKeyboardShortcuts(state: ShortcutState, onAction: (action: ShortcutAction) => void): void {
  // Read through a ref so the listener is attached once rather than swapped on
  // every render as the room state and the callback change identity.
  const latest = useRef({ state, onAction });

  useEffect(() => {
    latest.current = { state, onAction };
  });

  useEffect(() => {
    function handle(event: KeyboardEvent) {
      const current = latest.current;
      const action = shortcutFor(event.key, {
        ...current.state,
        typing: isTyping(event.target),
        modified: event.metaKey || event.ctrlKey || event.altKey,
      });
      if (action === null) return;

      // Backspace would otherwise navigate back on some setups.
      event.preventDefault();
      current.onAction(action);
    }

    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, []);
}
